import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import useNCZICommandProcess from '../../../Hooks/useNCZICommandProcess';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import withForm from '../../../../../Components/Form/withForm';
import moment, { Moment } from 'moment-timezone';
import { NCZIEntry } from '../../../Interfaces/ncziTypes';
import DatePicker from '../../../../../Components/Base/DatePicker/DatePicker';
import FormControl from '../../../../../Components/Form/FormControl';
import { yupMoment } from '../../../../../Components/Utils/Validation';
import FormTitle from '../../../../../Components/Form/FormTitle';
import FormContainer from '../../../../../Components/Form/FormContainer';
import InputWrapper from '../../../../../Components/Form/components/InputWrapper/InputWrapper';
import NCZIReferenceSelectField from '../../ReferenceSelect/NCZIReferenceSelectField';
import { NCZI_OID } from '../../../Enums/ncziOid.enum';
import CheckboxField from '../../../../../Components/Form/components/CheckboxField';
import InputField from '../../../../../Components/Form/components/InputField';
import FormMultiplier from '../../../../../Components/Form/FormMultiplier';
import TimePicker from '../../../../../Components/Base/TimePicker/TimePicker';
import RemoveButton from '../../../../../Components/Button/RemoveButton';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { GetPatientDPN } from '../../../Commands/GetPatientDPN';
import { useEffectAsync } from '../../../../../helpers';
import TextAreaField from '../../../../../Components/Form/components/TextAreaField';
import NCZIReferenceSearchSelectField from '../../ReferenceSearchSelect/NCZIReferenceSearchSelectField';
import { NCZIReferenceSelectItem, NCZIReferenceValidator } from '../../ReferenceSearchSelect/NCZIReferenceSearchSelect';
import Loading from '../../../../../Components/Loading';
import { WritePatientDPNChange } from '../../../Commands/WritePatientDPNChange';
import useNotification from '../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../Hooks/useTranslation';

//@ts-ignore
import styles from './ElectronicDPNChangeForm.module.scss';
import SelectField from '../../../../../Components/Form/components/SelectField';
import { SelectValue } from '../../../../../Components/Base/Select/Select';

export interface ElectronicDPNChangeFormValues {
	diagnosis: {
		code: NCZIReferenceSelectItem;
		validFrom: Moment | undefined;
	};
	incapableFrom: Moment;
	capableFrom: Moment | undefined;
	incapableReason: {
		reason: NCZIReferenceSelectItem;
		alcoholConsumption: SelectValue;
		validFrom: Moment | undefined;
	};
	incapableDuration: {
		anticipatedTerminationDate: Moment;
		note: string | undefined;
	};
	note: string;
	outings: {
		dateFrom: Moment;
		timeFrom: Moment;
		timeTo: Moment;
	}[],
	phone: string | undefined;
	treatmentAbroad: boolean;
}

interface Props {
	onClose?: (needReload: boolean) => void;
	patientIdentifier: string;
	electronicConfirmation: NCZIEntry;
	preloadedData?: any;
}

const ElectronicDPNChangeForm: React.FC<Props> = props => {
	const { onClose, patientIdentifier, electronicConfirmation, preloadedData } = props;
	const [defaultsLoaded, setDefaultsLoaded] = useState<boolean>(false);
	const [defaultData, setDefaultData] = useState<any>(undefined);
	const {showNotificationSuccess, showNotificationAlert} = useNotification();
	const {t} = useTranslation();

	const alcoholOptions = [
		{
			value: 'null',
			label: t('base.shared.notSpecified') || '',
		},
		{
			value: 'false',
			label: t('base.no') || '',
		},
		{
			value: 'true',
			label: t('base.yes') || '',
		}
	];

	const [capableFromValue, setCapableFromValue] = useState<Moment|undefined>(undefined);
	const [anticipatedTerminationDateValue, setAnticipatedTerminationDateValue] = useState<Moment|undefined>(undefined);

	const validationSchema = yup.object({
		diagnosis: yup.object({
			code: NCZIReferenceValidator('nczi.electronicDPN.create.diagnosis.code'),
			validFrom: yupMoment('nczi.electronicDPN.create.diagnosis.validFrom', true)
				.test(
					'diagnosis-from-incapableFrom',
					'nczi.electronicDPN.create.validation.minFromIncapableFrom',
					value => {
						if(!value) {
							return true;
						}
						const differenceInDays = value.diff(moment(defaultData.incapableFrom), 'days');
						return differenceInDays >= 0;
					}
				)
		}),
		incapableDuration: yup.object({
			anticipatedTerminationDate: yupMoment('nczi.electronicDPN.create.incapableDuration.anticipatedTerminationDate')
				.test(
					'anticipatedTerminationDate-from-incapableFrom',
					'nczi.electronicDPN.create.validation.minFromIncapableFrom',
					value => {
						if(!value) {
							return true;
						}
						const differenceInDays = value.diff(moment(defaultData.incapableFrom), 'days');
						return differenceInDays >= 0;
					}
				)
				.test(
					'anticipatedTerminationDate-after-capableFrom',
					'nczi.electronicDPN.create.validation.maxBeforeCapableFrom',
					value => {
						if(!value || !capableFromValue) {
							return true;
						}
						const differenceInDays = capableFromValue.diff(value, 'days');
						return differenceInDays >= 1;
					}
				),
			note: yup.string().max(2000, 'nczi.validation.max2000'),
		}),
		incapableReason: yup.object({
			reason: NCZIReferenceValidator('nczi.electronicDPN.create.incapableReason.reason'),
			alcoholConsumption: yup.object({
				label: yup.string(),
				value: yup.string()
			}).test(
				'alcohol-required',
				'nczi.electronicDPN.create.incapableReason.alcoholConsumption',
				(value, context) => {
					const isAlcoholRequired = ['02089917401', '02089917427'].indexOf(context.parent.reason.value.code) !== -1;
					return !isAlcoholRequired || (isAlcoholRequired && value.value !== 'null');
				}
			),
			validFrom: yupMoment('nczi.electronicDPN.create.incapableReason.validFrom', true)
				.test(
					'reason-from-incapableFrom',
					'nczi.electronicDPN.create.validation.minFromIncapableFrom',
					value => {
						if(!value) {
							return true;
						}
						const differenceInDays = value.diff(moment(defaultData.incapableFrom), 'days');
						return differenceInDays >= 0;
					}
				)
		}),
		capableFrom: yupMoment('nczi.electronicDPN.create.capableFrom', true)
			.test(
				'moment-not-older',
				'nczi.electronicDPN.create.validation.capableFromNotOlder',
				value => {
					if(!value) {
						return true;
					}
					const inputDate = moment();
					const differenceInDays = value.diff(inputDate, 'days');
					return differenceInDays >= 0;
				}
			),
		// issueDate: yupMoment('nczi.electronicDPN.create.issueDate', true),
		note: yup.string().max(2000, 'nczi.validation.max2000'),
		outings: yup.array().of(yup.object({
			dateFrom: yupMoment('nczi.electronicDPN.create.outings.dateFrom')
				.test(
					'outing-from-incapableFrom',
					'nczi.electronicDPN.create.validation.minFromIncapableFrom',
					value => {
						if(!value) {
							return true;
						}
						const differenceInDays = value.diff(moment(defaultData.incapableFrom), 'days');
						return differenceInDays >= 0;
					}
				),
			timeFrom: yupMoment('nczi.electronicDPN.create.outings.timeFrom'),
			timeTo: yupMoment('nczi.electronicDPN.create.outings.timeTo'),
		})).nullable(),
		phone: yup.string()
	});

	const formMethods = useForm<ElectronicDPNChangeFormValues>({
		defaultValues: {},
		resolver: yupResolver(validationSchema),
	});

	const capableFromWatcher = formMethods.watch('capableFrom');
	const anticipatedTerminationDateWatcher = formMethods.watch('incapableDuration.anticipatedTerminationDate');

	useEffect(() => {
		setCapableFromValue(capableFromWatcher);
		setAnticipatedTerminationDateValue(anticipatedTerminationDateWatcher);
	}, [capableFromWatcher, anticipatedTerminationDateWatcher]);

	const updateDefaultData = (data: any) => {
		setDefaultData(data);

		let alcoholConsumption = alcoholOptions[0];
		if(data.incapableReason?.alcoholConsumption !== null) {
			alcoholConsumption = data.incapableReason?.alcoholConsumption ? alcoholOptions[2] : alcoholOptions[1];
		}

		formMethods.setValue('incapableFrom', data.incapableFrom);
		formMethods.setValue('phone', data.phone);
		formMethods.setValue('note', data.note || '');
		formMethods.setValue('treatmentAbroad', data.treatmentAbroad);
		formMethods.setValue('incapableReason.validFrom', data.incapableReason?.validFrom ? moment(data.incapableReason.validFrom) : undefined);
		formMethods.setValue('incapableReason.alcoholConsumption', alcoholConsumption);
		if(data.incapableReason?.reason) {
			formMethods.setValue('incapableReason.reason', {
				label: data.incapableReason.reason.displayName,
				value: data.incapableReason.reason
			});
		}
		formMethods.setValue('incapableDuration.note', data.incapableDuration?.note || '');
		formMethods.setValue('incapableDuration.anticipatedTerminationDate', moment(data.incapableDuration.anticipatedTerminationDate));
		if(data.diagnosis?.code) {
			formMethods.setValue('diagnosis.code', {
				label: data.diagnosis.code.displayName,
				value: data.diagnosis.code
			});
		}
		formMethods.setValue('diagnosis.validFrom', data.diagnosis?.validFrom ? moment(data.diagnosis.validFrom) : undefined);
		if(data.outings.allowedOutings) {
			const outings: { dateFrom: Moment, timeFrom: Moment, timeTo: Moment }[] = [];
			for(const outing of data.outings.allowedOutings) {
				outings.push({
					dateFrom: moment(outing.dateFrom),
					timeFrom: moment(outing.timeFrom, 'HH:mm'),
					timeTo: moment(outing.timeTo, 'HH:mm')
				})
			}
			formMethods.setValue('outings', outings);
		}

		setDefaultsLoaded(true);
	}

	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: (commandId, result) => {
				if(!defaultsLoaded) {
					updateDefaultData(result.data);
				} else {
					if(result?.result?.message) {
						showNotificationAlert({
							content: result.result.message
						})
					} else {
						if(result) {
							showNotificationSuccess({
								content: t('nczi.result.updateSuccess')
							});
						}
						onClose?.(true);
					}
				}
			},
			hideSuccess: true
		});

	const handleSubmit = async (
		values: ElectronicDPNChangeFormValues,
	) => {
		// let changeDate = moment();
		// const incapableFrom = moment(values.incapableFrom);
		// if(changeDate.diff(incapableFrom, 'days') < 0) {
		// 	changeDate = incapableFrom;
		// }

		let alcoholConsumption = undefined;
		if(values.incapableReason.alcoholConsumption.value !== 'null') {
			alcoholConsumption = values.incapableReason.alcoholConsumption.value === 'true';
		}

		const finalValues = {
			createdAt: defaultData.createdAt,
			versionStatus: defaultData.versionStatus,
			sensitivity: defaultData.sensitivity,
			uncertaintyExpressed: false,
			incapableFrom: defaultData.incapableFrom,
			capableFrom: values.capableFrom?.format('YYYY-MM-DD') || undefined,
			issueDate: defaultData.issueDate,
			treatmentAbroad: defaultData.treatmentAbroad,
			note: values.note || undefined,
			outings: values.outings.length > 0 ? {
				allowedOutings: values.outings.map(outing => ({
					dateFrom: outing.dateFrom.format('YYYY-MM-DD'),
					timeFrom: outing.timeFrom.format('HH:mm'),
					timeTo: outing.timeTo.format('HH:mm'),
				}))
			} : undefined,
			placeOfStay: undefined,
			incapableReason: {
				reason: values.incapableReason.reason.value,
				alcoholConsumption,
				validFrom: values.incapableReason.validFrom?.format('YYYY-MM-DD') || undefined
			},
			incapableDuration: {
				anticipatedTerminationDate: values.incapableDuration.anticipatedTerminationDate.format('YYYY-MM-DD'),
				note: values.incapableDuration.note || undefined
			},
			diagnosis: {
				code: values.diagnosis.code.value,
				validFrom: values.diagnosis.validFrom?.format('YYYY-MM-DD')
			},
			changeDate: moment().format('YYYY-MM-DD'),
			phone: values.phone || undefined,
			previousVersion: defaultData.previousVersion,
			versionSetId: defaultData.versionSetId || undefined,
			insuranceExemption: defaultData.insuranceExemption.length > 0 ? defaultData.insuranceExemption : undefined
		};

		const writeCommand = WritePatientDPNChange({
			patientCode: patientIdentifier,
			confirmation: finalValues,
		});

		await processCommand(writeCommand);
	};

	useEffectAsync(async () => {
		if(!preloadedData) {
			const GetDetailCommand = GetPatientDPN({
				patientCode: patientIdentifier,
				confirmationId: electronicConfirmation.recordId!
			});

			await processCommand(GetDetailCommand);
		} else {
			updateDefaultData(preloadedData);
		}
	}, []);

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	if(!defaultsLoaded) {
		return <Loading></Loading>;
	}

	return (
		<div>
			<FormWrapper formMethods={formMethods}>
				<div className={'row'}>
					<div className={'col-6'}>
						<FormControl
							label='nczi.electronicDPN.create.incapableFrom'
							name='incapableFrom'
							isRequired>
							<DatePicker disabled />
						</FormControl>
					</div>
					<div className={'col-6'}>
						<FormControl
							label='nczi.electronicDPN.create.capableFrom'
							name='capableFrom'
							isRequired>
							<DatePicker />
						</FormControl>
					</div>
				</div>
				<InputWrapper>
					<TextAreaField
						label='nczi.electronicDPN.create.note'
						name='note'
						isRequired={false}
						charactersLimit={2000}
					/>
				</InputWrapper>
				<InputWrapper>
					<CheckboxField
						tx='nczi.electronicDPN.create.treatmentAbroad'
						name='treatmentAbroad'
						isRequired
						firstUpper
						isDisabled
					/>
				</InputWrapper>
				<FormTitle
					tx='nczi.electronicDPN.create.incapableReason.title'
					type='default'
				/>
				<FormContainer name={"incapableReason"} settings={{ showLine: true }}>
					<InputWrapper>
						<NCZIReferenceSelectField
							label='nczi.electronicDPN.create.incapableReason.reason'
							name='reason'
							oid={NCZI_OID.DPN_REASON}
							isRequired
						/>
					</InputWrapper>
					<SelectField
						label='nczi.electronicDPN.create.incapableReason.alcoholConsumption'
						name='alcoholConsumption'
						options={alcoholOptions}
						isRequired
					/>
					<FormControl
						label='nczi.electronicDPN.create.incapableReason.validFrom'
						name='validFrom'
						isRequired>
						<DatePicker />
					</FormControl>
				</FormContainer>
				<FormTitle
					tx='nczi.electronicDPN.create.incapableDuration.title'
					type='default'
				/>
				<FormContainer name={"incapableDuration"} settings={{ showLine: true }}>
					<FormControl
						label='nczi.electronicDPN.create.incapableDuration.anticipatedTerminationDate'
						name='anticipatedTerminationDate'
						isRequired>
						<DatePicker />
					</FormControl>
					<InputWrapper>
						<TextAreaField
							label='nczi.electronicDPN.create.incapableDuration.note'
							name='note'
							isRequired={false}
							charactersLimit={2000}
						/>
					</InputWrapper>
				</FormContainer>
				<FormTitle
					tx='nczi.electronicDPN.create.diagnosis.title'
					type='default'
				/>
				<FormContainer name={"diagnosis"} settings={{ showLine: true }}>
					<InputWrapper>
						<NCZIReferenceSearchSelectField
							label='nczi.electronicDPN.create.diagnosis.code'
							name='code'
							oid={NCZI_OID.DIAGNOSIS}
							showCode
							isRequired
						/>
					</InputWrapper>
					<FormControl
						label='nczi.electronicDPN.create.diagnosis.validFrom'
						name='validFrom'
						isRequired>
						<DatePicker />
					</FormControl>
				</FormContainer>
				<InputWrapper>
					<InputField
						label='nczi.electronicDPN.create.phone'
						name='phone'
						isRequired={false}
					/>
				</InputWrapper>
				<FormTitle
					tx='nczi.electronicDPN.create.outings.title'
					type='default'
				/>
				<FormMultiplier name='outings' addButtonTitle={'nczi.electronicDPN.create.outings.add'}>
					{({ remove, index, itemsCount }) => (
						<FormContainer settings={{ showLine: true }}>
							<div className={'row'}>
								<div className={'col-4'}>
									<FormControl
										label='nczi.electronicDPN.create.outings.dateFrom'
										name='dateFrom'
										isRequired>
										<DatePicker />
									</FormControl>
								</div>
								<div className={'col-3'}>
									<FormControl
										label='nczi.electronicDPN.create.outings.timeFrom'
										name='timeFrom'
										isRequired>
										<TimePicker />
									</FormControl>
								</div>
								<div className={'col-3'}>
									<FormControl
										label='nczi.electronicDPN.create.outings.timeTo'
										name='timeTo'
										isRequired>
										<TimePicker />
									</FormControl>
								</div>
								<div className={'col-2'}>
									<div className={styles.removeItemWrapper}>
										<RemoveButton
											onClick={() => {
												remove(index);
											}}
										/>
									</div>
								</div>
							</div>
						</FormContainer>
					)}
				</FormMultiplier>
				<div className='mt-4'>
					<SubmitButton
						title='component.nczi.setup.save'
						onSubmit={handleSubmit}
					/>
				</div>
			</FormWrapper>
		</div>
	);
};

export default withForm(ElectronicDPNChangeForm);
